import { CKButton, CKLayoutHorizontal } from 'clearkit';
import { FC } from 'react';
import { Helmet } from 'react-helmet';

import { useStatusToast } from '~/components/unified/hooks';
import { SettingsCard } from '~/components/unified/SettingsCard';
import { useEntitlements } from '~/hooks/account';

import { FormUserProfile } from './FormUserProfile';
import { UserProfile } from './types';
import { useCurrentUser } from './useCurrentUser';

export const UserAccountSettings: FC = () => {
  const currentUser = useCurrentUser();
  const { hasAccessSettingsTeamCreation } = useEntitlements();

  const { addSuccessToast, addErrorToast } = useStatusToast();

  const handleUpdateUser = async (value: UserProfile) => {
    const res = await currentUser.updateProfile({
      variables: {
        input: {
          firstName: value.firstName,
          lastName: value.lastName,
          mailing: value.mailing,
        },
      },
    });

    if (res.data?.updateUserProfile?.errors?.length) {
      return addErrorToast({
        description: 'We were unable to update your profile, please try again.',
        heading: 'Profile update failed',
      });
    }

    addSuccessToast({
      description: 'Your profile information was updated.',
      heading: 'Profile updated',
    });
  };

  if (currentUser.error) {
    return (
      <>
        <h1>There was an error fetching your profile</h1>
        <CKButton onClick={() => currentUser.refetch()}>Retry</CKButton>
      </>
    );
  }

  if (currentUser.loading) return null;

  const isMFAEnforced = currentUser.account?.mfaEnforced;

  return (
    <>
      <Helmet>
        <title>User Profile</title>
      </Helmet>
      <div className="space-y-8">
        <FormUserProfile
          onChange={handleUpdateUser}
          value={currentUser.user!}
        />
        {hasAccessSettingsTeamCreation ? (
          <SettingsCard>
            <SettingsCard.Header className="pb-4">
              <SettingsCard.Heading>Security</SettingsCard.Heading>
            </SettingsCard.Header>
            <SettingsCard.Footer className="flex items-center justify-between">
              <CKLayoutHorizontal>
                <span className="relative flex w-10 h-10 bg-gray-100 rounded-full shrink-0">
                  <img
                    alt="lock"
                    className="w-6 h-6 m-auto"
                    src="https://cdn.clearbit.com/images/lock-icon.svg"
                  />
                </span>
                <p>
                  Multi-factor authentication is{' '}
                  {isMFAEnforced ? (
                    ''
                  ) : (
                    <span className="font-medium">not</span>
                  )}{' '}
                  currently enabled
                </p>
              </CKLayoutHorizontal>
              {isMFAEnforced ? null : (
                <CKButton
                  as="a"
                  href="https://clearbit.com/mfa/setup"
                  target="_blank"
                  variant="bold"
                  variantColor="blue"
                >
                  Set up now
                </CKButton>
              )}
            </SettingsCard.Footer>
          </SettingsCard>
        ) : null}
      </div>
    </>
  );
};
